import Icon from "~/components/shared/Icon";
import { State, WaitStep } from "~/lib/pipeline";
import { WAIT_STEP_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";

interface Props {
  step: WaitStep;
  depth?: number;
}

// Indent the start of the horizontal line so it picks up exactly where the
// parent's tree connector ends (CONNECTOR_RIGHT in TreeItem). This prevents
// the wait step's line from crossing through ancestor vertical guide lines.
// Matches the chevron column (mr-1 w-6 = 1.75rem) + DEPTH_SPACER from StepRow.
const DEPTH_INDENT: Record<number, string> = {
  1: "calc(1.75rem + 1.75rem)",
  2: "calc(1.75rem + 3.5rem)",
};

export function WaitStepItem({ step, depth = 0 }: Props) {
  return (
    <div className="flex h-full w-full items-center pl-1.5 pr-1.5">
      {DEPTH_INDENT[depth] && <div className="shrink-0" style={{ width: DEPTH_INDENT[depth] }} />}
      {/*
        -mb-px shifts this row's mid-line down 1px so the border-b paints at
        y=18 (in a 36px row), aligning with the TreeItem connector at top:50%.
        Without it, items-center centers the 1px border-box at y=17.5, which
        rounds 1px high relative to the connector.
      */}
      <div className="-mb-px flex flex-1 items-center gap-2">
        <div className="flex-1 border-b border-gray-400" />
        <HoverCard openDelay={500} closeDelay={100}>
          <HoverCardTrigger asChild={true}>
            <span className="text-charcoal-300 hover:text-charcoal-800 flex items-center transition-colors">
              <Icon
                icon={
                  step.state === State.Finished
                    ? "heroicons/outline/chevron-down"
                    : "heroicons/solid/pause"
                }
                className="size-4 shrink-0"
              />
            </span>
          </HoverCardTrigger>
          <HoverCardPortal>
            <HoverCardContent className="z-50" hideArrow={true} size="max-sm" side="top">
              <p className="text-charcoal-600 m-0 text-xs font-medium">Wait</p>
            </HoverCardContent>
          </HoverCardPortal>
        </HoverCard>
        <div className="flex-1 border-b border-gray-400" />
      </div>
    </div>
  );
}

export const createWaitStepNode = (step: WaitStep): TreeNode => ({
  id: step.uuid,
  height: WAIT_STEP_HEIGHT,
  render: ({ depth }) => <WaitStepItem step={step} depth={depth} />,
});
